import React, { useState } from 'react';
import {
  IonContent,
  IonPage,
  IonCard,
  IonCardContent,
  IonList,
  IonItem,
  IonLabel,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonMenuButton,
  IonTitle,
  IonText,
  IonDatetime,
  IonIcon,
} from '@ionic/react';
import { useQuery } from '@tanstack/react-query';
import { GetAppointmentsByDate } from '../data/api';
import { Link, RouteComponentProps } from 'react-router-dom';
import { arrowDownCircle, locationOutline, timeOutline, informationCircleOutline } from 'ionicons/icons';
import '../shared/SchedulingShared.css';

interface StateProps {
  title: string;
}

interface RouteProps extends RouteComponentProps<{ dateValue: string }> {}
interface GenericProps extends RouteProps, StateProps {}

const Fingerprint311: React.FC<GenericProps> = ({ match }) => {
  let today = new Date(new Date().toDateString());
  if (match.params.dateValue != null) {
    const parsed = new Date(match.params.dateValue);
    if (!Number.isNaN(parsed.getTime())) {
      today = new Date(parsed.toDateString());
    }
  }

  const [selectedDate, setSelectedDate] = useState<string>(today.toISOString());

  function GetDatetList(props: any) {
    const myQuery = useQuery({
      queryKey: ['GetAppointmentsByDateF', props.myType, props.myDate],
      queryFn: () => GetAppointmentsByDate(props.myType, props.myDate),
    });
    return (
      <div>
        {myQuery.isLoading && <IonItem>Loading</IonItem>}
        {myQuery.isSuccess && myQuery.data.length === 0 && (
          <IonItem lines="none">
            <IonLabel color="medium">No time slots for this date.</IonLabel>
          </IonItem>
        )}
        {myQuery.isSuccess &&
          myQuery.data.map((dateString: any) => (
            <IonItem key={dateString.aptkey}>
              <div
                className="scheduling-slot-indicator"
                data-aptsts={dateString.aptsts}
                slot="start"
              >
                &nbsp;
              </div>
              <div>
                {dateString.aptsts === 'A' ? (
                  <div>
                    <Link
                      to={{
                        pathname:
                          '/tabs/fingerprint311apt/' +
                          dateString.aptkey.toString().replaceAll('/', '|'),
                      }}
                    >
                      <IonLabel>
                        {dateString.aptst}-{dateString.apted}
                      </IonLabel>
                    </Link>
                  </div>
                ) : (
                  <div className="scheduling-slot-label-muted">
                    <IonLabel>
                      {dateString.aptst}-{dateString.apted}
                    </IonLabel>
                  </div>
                )}
              </div>
              <div slot="end" className="scheduling-slot-name-muted">
                <IonLabel>{dateString.aptname}</IonLabel>
              </div>
            </IonItem>
          ))}
      </div>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle color="primary">311 Fingerprint Services</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent color="light" className="passport-page">
        <div className="passport-layout">
              <IonCard className="passport-info-panel">
                <IonCardContent className="passport-info-card">
                  <div className="passport-info-columns">
                    <section className="passport-info-pane">
                      <h2 className="passport-info-pane-title">
                        <IonIcon icon={locationOutline} aria-hidden="true" />
                        Location
                      </h2>
                      <IonText>
                        <p className="passport-info-line">
                          <strong>311 Help Desk (Room 110)</strong>
                        </p>
                        <p className="passport-info-line">
                          56 North State Street
                          <br />
                          Orem, UT 84057
                        </p>
                      </IonText>
                    </section>

                    <div className="passport-info-divider" aria-hidden="true" />

                    <section className="passport-info-pane">
                      <h2 className="passport-info-pane-title">
                        <IonIcon icon={timeOutline} aria-hidden="true" />
                        Hours of operation
                      </h2>
                      <IonText className="passport-info-hours">
                        <p>
                          <span className="passport-hours-days">Monday, Tuesday, Thursday:</span> 8:00 AM to
                          5:00 PM
                        </p>
                        <p>
                          <span className="passport-hours-days">Wednesday:</span> 8:30 AM to 5:00 PM
                        </p>
                        <p>
                          <span className="passport-hours-days">Friday:</span> 8:00 AM to 12:00 PM &amp; 2:00
                          PM to 5:00 PM
                        </p>
                        <p>
                          <span className="passport-hours-days">Saturday &amp; Sunday:</span>{' '}
                          <span className="passport-hours-closed">Closed</span>
                        </p>
                      </IonText>
                    </section>
                  </div>

                  <div className="passport-info-note" role="note">
                    <IonIcon icon={informationCircleOutline} aria-hidden="true" />
                    <span>
                      To schedule an appointment, click a{' '}
                      <strong className="passport-info-note-slot">
                        <span className="passport-info-note-green">green</span> open slot
                      </strong>{' '}
                      below.
                    </span>
                  </div>

                  <div className="passport-info-details">
                    <IonText>
                      <p>
                        To assist you best, please make sure you have selected the correct scheduling service.
                        Fingerprint 311 is for employment purposes and Fingerprint Court is for those with a court
                        case at the Orem Justice Court. Use the calendar to change the date, then utilize the
                        color coding to determine an open slot. Once you find a time that works for you, click it
                        and complete the needed information.
                      </p>
                      <p>
                        Fingerprinting is by appointment only. Only the person being fingerprinted will be allowed
                        in the fingerprinting room. We do not fingerprint anyone under the age of 16. Please do not
                        bring minor children to your fingerprinting appointment as we will not be able to process
                        your fingerprints and you will need to reschedule.
                      </p>
                      <p>
                        You will receive a confirmation email that your appointment has been set. If you have any
                        questions contact us at <strong>801-229-7275</strong>.
                      </p>
                    </IonText>
                  </div>
                </IonCardContent>
              </IonCard>

            <div className="passport-schedule-row">
              <IonCard>
                <IonCardContent>
                  <IonItem lines="none">
                    <IonIcon slot="start" icon={arrowDownCircle} color="primary" />
                    <IonLabel>Selected Date</IonLabel>
                  </IonItem>
                  <IonItem mode="ios" lines="none">
                    <IonDatetime
                      value={selectedDate}
                      presentation="date"
                      onIonChange={(e: any) => {
                        setSelectedDate(e.detail.value!);
                      }}
                      placeholder="Select Time"
                    />
                  </IonItem>
                </IonCardContent>
              </IonCard>
              <IonCard>
                <IonCardContent>
                  <IonList>
                    <GetDatetList myType="F" myDate={selectedDate} />
                  </IonList>
                </IonCardContent>
              </IonCard>
            </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Fingerprint311;
